// material-ui
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import { useApiBuildInfo } from 'api/vektra/hooks';
import { withAlpha } from 'utils/colorUtils';

// ==============================|| MAIN LAYOUT - FOOTER ||============================== //

// The default Able Pro footer linked back to the template's home/docs/support and a
// Phoenixcoded credit line — none of which belong in the shipped app. What the bar carries
// instead is the identity of the two builds serving the page.
//
// The two lines are established at different moments, deliberately. The client's values are baked
// in by vite.config.mts, so they are constant for one deployed bundle — across reloads, across an
// open tab, and across time zones. The API's are fetched from the API answering right now, because
// deploy:api and deploy:client are separate manual jobs and rollback:api can put an arbitrary older
// tag on the host: the two halves routinely run different commits, and the whole point of the
// second line is that the mismatch is visible rather than inferred from behaviour.
//
// `dev` marks a build with no commit metadata (one made outside the CI path). It is shared by both
// lines on purpose — one rule to learn, not two.
const NO_COMMIT = 'dev';

export default function Footer() {
  const { data, isPending, isError } = useApiBuildInfo();

  // A failed read and a resolved-but-empty payload are the same fact to a reader: this page cannot
  // say which API answered it. What must never appear is a blank, the client's own values in the
  // API line's place, or a value left over from an earlier page load.
  const apiLine = isPending ? '…' : isError || !data ? 'unavailable' : `${data.buildTime ?? NO_COMMIT} · ${data.commit ?? NO_COMMIT}`;

  return (
    // The colour lives here rather than on each Typography so the two lines cannot drift apart:
    // they describe the same kind of thing and must stay legible on the same terms.
    //
    // Light mode uses `text.secondary` unchanged — measured 5.21:1, comfortably past WCAG AA.
    // Dark mode does not: the token is the same base colour at 0.45 alpha over a near-black
    // background (palette.ts:48), which measures 4.32:1 — under the 4.5:1 AA needs for text this
    // size. Raising the alpha on that same base keeps the footer subordinate to body text (still
    // well below `text.primary`'s 0.87) while clearing the bar. Overriding here rather than in
    // palette.ts is deliberate: `text.secondary` is template-wide, and this change has only
    // measured the footer.
    <Stack
      sx={(theme) => ({
        alignItems: 'flex-end',
        px: 1,
        py: 1.5,
        mt: 'auto',
        color: 'text.secondary',
        ...theme.applyStyles('dark', { color: withAlpha(theme.vars.palette.secondary.darker, 0.6) })
      })}
    >
      <Typography variant="caption">
        built {__BUILD_TIME__} · {__GIT_COMMIT__}
      </Typography>
      <Typography variant="caption">api {apiLine}</Typography>
    </Stack>
  );
}
